import {
  ELEMENT_BLOCKQUOTE,
  ELEMENT_LI,
  ELEMENT_OL,
  ELEMENT_UL,
  ELEMENT_LIC,
  ELEMENT_PARAGRAPH,
} from "./constants";

import type { TElement } from "@udecode/plate-core";
import type { EditorValue } from "./types";

type ParseableNode = TElement & {
  parentType?: string;
  previousSibling?: ParseableNode;
};

const LIST_TYPES = [ELEMENT_OL, ELEMENT_UL];

const isTextNode = (node: ParseableNode) => {
  return typeof node.text === "string";
};

/**
 * Convert a Slate node to markdown
 */
function parse(node: ParseableNode, opts: { listDepth?: number }) {
  const { listDepth = 0 } = opts;

  const text = (node.text ?? "") as string;
  const type = (node.type ?? "") as string;

  let children = text;

  if (!isTextNode(node)) {
    // Recurse children
    children = node.children
      .map((c, i, a) => {
        return parse(
          {
            ...c,
            parentType: type,
            previousSibling: a[i - 1],
          } as ParseableNode,
          {
            // Track depth of nested lists so we can add proper spacing
            listDepth: (LIST_TYPES as string[]).includes(
              (c.type ?? "") as string,
            )
              ? listDepth + 1
              : listDepth,
          },
        );
      })
      .join("")
      .trimEnd();
  }

  if (children === "") return;

  if (isTextNode(node)) {
    if (node.underline && node.bold && node.italic) {
      children = `${formatTextNode(children, "<u>***$1***</u>")}`;
    } else if (node.bold && node.italic) {
      children = formatTextNode(children, "***$1***");
    } else {
      if (node.bold) {
        children = formatTextNode(children, "**$1**");
      }

      if (node.italic) {
        children = formatTextNode(children, "*$1*");
      }

      if (node.underline) {
        children = formatTextNode(children, "<u>$1</u>");
      }
    }
  }

  // Handle elements
  switch (type) {
    case ELEMENT_BLOCKQUOTE:
      return `\n> ${children.trim()}\n`;
    case ELEMENT_OL:
    case ELEMENT_UL:
      return `\n${children}\n`;
    case ELEMENT_LI: {
      const isOL = node && node.parentType === ELEMENT_OL;
      const treatAsLeaf =
        node.children.length === 1 &&
        (isTextNode(node.children[0] as ParseableNode) ||
          node.children[0].type === ELEMENT_LIC);

      const spacer = listDepth ? "    ".repeat(listDepth) : "";

      return `${spacer}${isOL ? "1." : "-"} ${children}${
        treatAsLeaf ? "\n" : ""
      }`;
    }
    case ELEMENT_PARAGRAPH:
      if (node.previousSibling?.type === ELEMENT_BLOCKQUOTE) {
        return `\n${children}\n`;
      }

      return `${children}\n`;
    default:
      return children;
  }
}

/**
 * Wraps a text node in markdown sugar
 */
function formatTextNode(text: string, tmpl: string) {
  const trimmedString = text.trim();
  const formattedString = tmpl.replace(/\$1/, trimmedString);

  // Return early if there's no whitespace in the string
  if (trimmedString.length === text.length) {
    return formattedString;
  }

  // Replace the non-whitespace content of the string
  return text.replace(trimmedString, formattedString);
}

/**
 * Slate -> Markdown
 */
export function serialize(value: EditorValue) {
  return value
    .map((v, i, a) => parse({ ...v, previousSibling: a[i - 1] }, {}))
    .join("")
    .trimStart();
}
